import { createContext, useContext, useMemo, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";

// Where the user currently is in the CRM, so a note or task can be filed
// against it.
//
// WHY A PROVIDER AND NOT JUST THE URL: in the Media Buying module the client,
// project and campaign drills are DIALOGS, not routes — the URL stays
// /media-buying/overview whether you are looking at nothing or deep inside a
// client. So the URL alone cannot answer "what am I looking at". Pages that
// know their subject publish it here; everything else falls back to what the
// route does encode (/clients/:id and friends).
const CrmContextCtx = createContext({ context: null, setCrmContext: () => {} });

// Routes that DO carry their subject in the path.
const fromPath = (pathname) => {
  const m = /^\/(clients|financialclient|leads|myleads|prefixes|teammembers)\/([^/]+)/.exec(pathname || "");
  if (!m) return null;
  const [, seg, ref] = m;
  const type = seg === "prefixes" ? "project" : seg === "teammembers" ? "buyer" : "client";
  return { type, ref, label: null, path: pathname };
};

export function CrmContextProvider({ children }) {
  const location = useLocation();
  // Published by a page/dialog that knows better than the URL does.
  const [published, setPublished] = useState(null);

  const setCrmContext = useCallback((ctx) => {
    // Guarded: a couple of tests replace window.location wholesale, and a
    // context provider is not worth crashing a page over.
    const here = typeof window !== "undefined" && window.location ? window.location.pathname : null;
    setPublished(ctx ? { ...ctx, path: ctx.path || here } : null);
  }, []);

  const value = useMemo(() => ({
    // A published context wins: it is more specific than anything the URL knows.
    context: published || fromPath(location.pathname),
    setCrmContext,
  }), [published, location.pathname, setCrmContext]);

  return <CrmContextCtx.Provider value={value}>{children}</CrmContextCtx.Provider>;
}

export const useCrmContext = () => useContext(CrmContextCtx);

// For a page/dialog to declare what it is showing. Call with null on close.
export function usePublishCrmContext() {
  return useCrmContext().setCrmContext;
}
